import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, Search, Tag, Tile, Toggle } from '@carbon/react';
import { Logout, Renew } from '@carbon/icons-react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { EmptyState, ErrorState, InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';

export type ActivePlayer = {
  id: number;
  name: string;
  location: string;
  balance: number;
  onlineSince: string;
  isTest?: boolean;
};

type ActivePlayersResponse = {
  items: ActivePlayer[];
  total: number;
  degraded?: boolean;
  at: string;
};

export function ActivePlayersPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [search, setSearch] = useState('');
  const [kickTarget, setKickTarget] = useState<ActivePlayer | null>(null);
  const [notice, setNotice] = useState<{ kind: 'success' | 'info' | 'error' | 'warning'; title: string; subtitle?: string } | null>(null);

  const canKick = can('users.kick');

  const { data, isLoading, error, refetch, isFetching } = useQuery<ActivePlayersResponse>({
    queryKey: ['active-players'],
    queryFn: () => api.get<ActivePlayersResponse>('/api/admin/users/active'),
    refetchInterval: autoRefresh ? 10000 : false,
    refetchIntervalInBackground: false,
  });

  const kick = useMutation({
    mutationFn: (id: number) => api.post<{ ok: boolean; so_socket_he_a: number; so_socket_he_b: number | null }>(`/api/admin/users/${id}/kick-lobby`),
    onSuccess: (res, id) => {
      qc.invalidateQueries({ queryKey: ['active-players'] });
      const target = kickTarget;
      setKickTarget(null);
      const targetName = target ? target.name || `#${target.id}` : `#${id}`;
      const totalClosed = (res.so_socket_he_a || 0) + (res.so_socket_he_b || 0);
      if (res.so_socket_he_b === null) {
        setNotice({
          kind: 'warning',
          title: `Đã kick ${targetName} về sảnh (Hệ B chưa phản hồi)`,
          subtitle: `Hệ A đã đóng ${res.so_socket_he_a} kết nối. Hệ B mất kết nối hoặc timeout khi nhận lệnh kick.`,
        });
      } else if (totalClosed > 0) {
        setNotice({
          kind: 'success',
          title: `Đã kick ${targetName} về sảnh`,
          subtitle: `Đã đóng ${totalClosed} kết nối game (Hệ A: ${res.so_socket_he_a}, Hệ B: ${res.so_socket_he_b}). Client sẽ tải lại vào sảnh.`,
        });
      } else {
        setNotice({
          kind: 'info',
          title: `Đã gửi lệnh kick cho ${targetName}`,
          subtitle: 'Người chơi không có kết nối game mở nào (đang ở sảnh hoặc vừa ngắt kết nối).',
        });
      }
    },
    onError: (err) => {
      setNotice({
        kind: 'error',
        title: 'Kick không thành công',
        subtitle: err instanceof Error ? err.message : String(err),
      });
    },
  });

  const items = useMemo(() => data?.items ?? [], [data?.items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      String(p.id).includes(q) ||
      p.location.toLowerCase().includes(q)
    );
  }, [items, search]);

  const stats = useMemo(() => {
    let inGame = 0;
    let inLobby = 0;
    let realBalance = 0;
    let testBalance = 0;
    for (const p of items) {
      if (p.location === 'Sảnh') inLobby++;
      else inGame++;
      if (p.isTest) testBalance += p.balance;
      else realBalance += p.balance;
    }
    return { total: items.length, inGame, inLobby, realBalance, testBalance };
  }, [items]);

  if (error && !data) {
    return (
      <div className="page">
        <PageHeader title="Người chơi đang hoạt động" />
        <ErrorState error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="Người chơi đang hoạt động"
        lead="Danh sách người chơi đang kết nối thời gian thực trên hệ A và hệ B. Tự động cập nhật mỗi 10 giây."
        actions={
          <div className="row" style={{ alignItems: 'center', gap: '1rem' }}>
            <Toggle
              id="auto-refresh-toggle"
              size="sm"
              labelText=""
              labelA="Tự cập nhật: Tắt"
              labelB="Tự cập nhật: Bật"
              toggled={autoRefresh}
              onToggle={(t) => setAutoRefresh(t)}
            />
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Renew}
              disabled={isFetching}
              onClick={() => refetch()}
            >
              {isFetching ? 'Đang tải...' : 'Làm mới'}
            </Button>
          </div>
        }
      />

      {data?.degraded ? (
        <InlineNotification
          kind="warning"
          lowContrast
          title="Mất kết nối một phần"
          subtitle="Không thể đọc đầy đủ dữ liệu từ Hệ A hoặc Hệ B. Dữ liệu đang hiển thị có thể chưa đầy đủ."
        />
      ) : null}

      {notice ? (
        <InlineNotification
          kind={notice.kind}
          lowContrast
          title={notice.title}
          subtitle={notice.subtitle}
          onClose={() => setNotice(null)}
        />
      ) : null}

      <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <Tile>
          <div className="muted" style={{ fontSize: '0.85rem' }}>Tổng đang online</div>
          <div className="num" style={{ fontSize: '1.75rem', fontWeight: 600 }}>{stats.total}</div>
        </Tile>
        <Tile>
          <div className="muted" style={{ fontSize: '0.85rem' }}>Đang trong game</div>
          <div className="num" style={{ fontSize: '1.75rem', fontWeight: 600, color: 'var(--cds-support-info, #0f62fe)' }}>{stats.inGame}</div>
        </Tile>
        <Tile>
          <div className="muted" style={{ fontSize: '0.85rem' }}>Đang ở sảnh</div>
          <div className="num" style={{ fontSize: '1.75rem', fontWeight: 600 }}>{stats.inLobby}</div>
        </Tile>
        <Tile>
          <div className="muted" style={{ fontSize: '0.85rem' }}>Tổng số dư online</div>
          <div className="num" style={{ fontSize: '1.4rem', fontWeight: 600, color: 'var(--cds-support-success, #24a148)' }}>
            <Money v={stats.realBalance} unit />
          </div>
          {stats.testBalance > 0 ? (
            <div className="muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
              Tài khoản test: <Money v={stats.testBalance} unit />
            </div>
          ) : null}
        </Tile>
      </div>

      <div className="panel stack" style={{ gap: '1rem' }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ maxWidth: '360px', width: '100%' }}>
            <Search
              size="sm"
              labelText="Tìm theo tên, ID hoặc vị trí"
              placeholder="Tìm theo tên, ID hoặc game..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
            />
          </div>
          <div className="muted" style={{ fontSize: '0.85rem' }}>
            Hiển thị <b>{filtered.length}</b> / {items.length} người chơi
          </div>
        </div>

        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Đang tải danh sách người chơi trực tuyến...</div>
        ) : !filtered.length ? (
          <EmptyState
            title={search ? 'Không tìm thấy người chơi phù hợp' : 'Không có người chơi nào đang hoạt động'}
            body={search ? 'Thử tìm với từ khóa khác.' : 'Hiện chưa có người chơi nào kết nối vào game.'}
          />
        ) : (
          <div className="table-scroll">
            <table className="plain">
              <thead>
                <tr>
                  <th>Người chơi</th>
                  <th>ID</th>
                  <th>Vị trí</th>
                  <th className="r">Số dư</th>
                  <th>Online từ</th>
                  {canKick ? <th className="r">Thao tác</th> : null}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const isLobby = p.location === 'Sảnh';
                  return (
                    <tr key={p.id}>
                      <td className="is-title">
                        {can('users.view') ? (
                          <Link className="cell-link" to={`/users/${p.id}`}>
                            <b>{p.name}</b>
                          </Link>
                        ) : (
                          <b>{p.name}</b>
                        )}
                        {p.isTest ? (
                          <Tag size="sm" type="purple" style={{ marginLeft: '0.5rem' }}>
                            Test
                          </Tag>
                        ) : null}
                      </td>
                      <td className="num muted">#{p.id}</td>
                      <td data-label="Vị trí">
                        <Tag size="sm" type={isLobby ? 'cool-gray' : 'teal'}>
                          {p.location}
                        </Tag>
                      </td>
                      <td className="r" data-label="Số dư">
                        <Money v={p.balance} unit />
                      </td>
                      <td data-label="Online từ">
                        <When v={p.onlineSince} />
                      </td>
                      {canKick ? (
                        <td className="r" data-label="Thao tác">
                          <Button
                            size="sm"
                            kind="danger--ghost"
                            renderIcon={Logout}
                            onClick={() => setKickTarget(p)}
                          >
                            Kick
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={Boolean(kickTarget)}
        danger
        modalHeading={`Kick ${kickTarget?.name || '#' + kickTarget?.id} về sảnh?`}
        primaryButtonText="Kick về sảnh"
        secondaryButtonText="Hủy"
        primaryButtonDisabled={kick.isPending}
        onRequestClose={() => {
          kick.reset();
          setKickTarget(null);
        }}
        onRequestSubmit={() => {
          if (kickTarget) kick.mutate(kickTarget.id);
        }}
      >
        <div className="stack" style={{ gap: '0.75rem' }}>
          <p>
            Chỉ đóng các kết nối game của người chơi <b>{kickTarget?.name}</b> (#{kickTarget?.id}) với bàn chơi / slot trên hệ A và hệ B.
          </p>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            Phiên đăng nhập vẫn giữ nguyên. Client của người chơi sẽ nhận mã đóng kết nối 4003 và tự động tải lại vào sảnh chính.
          </p>
          {kickTarget?.location && kickTarget.location !== 'Sảnh' ? (
            <p>
              Vị trí hiện tại: <Tag size="sm" type="teal">{kickTarget.location}</Tag>
            </p>
          ) : null}
          <InlineError error={kick.error} />
        </div>
      </Modal>
    </div>
  );
}
